import type { FC } from "react";
import { createElement, useEffect, useMemo, useState } from "react";

import { AutoCompareLegends } from "app/components/React/Legends/AutoCompareLegends";
import { BulletLegends } from "app/components/React/Legends/BulletLegends";
import { Legends } from "app/components/React/Legends/Legends";
import type { IDashboardWidgetInterface } from "app/components/widget/utils/DashboardWidgetsInterfaces-v2";
import { WidgetMessages } from "app/components/widget/widget-types/Widget";
import { CHART_COLORS } from "app/constants/ChartColors";
import { BenchmarkCompareLegendTemplate } from "app/dashboards/view-components/widget-legend/custom-legend/benchmarkCompareLegendTemplate";
import { ComparedDurationLegendTemplate } from "app/dashboards/view-components/widget-legend/custom-legend/comparedDurationLegendTemplate";
import { EngagementWidgetsLegendCompareTemplate } from "app/dashboards/view-components/widget-legend/custom-legend/engagementWidgetsLegendCompareTemplate";
import { EngagementWidgetsLegendSingleTemplate } from "app/dashboards/view-components/widget-legend/custom-legend/engagementWidgetsLegendSingleTemplate";
import { MmxDataLegendTemplate } from "app/dashboards/view-components/widget-legend/custom-legend/mmxDataLegendTemplate";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { fetchWebsiteImageQuery } from "app/services/sitesService/queries/sitesQueries";
import { SwTrack } from "app/services/SwTrack";

import { LegendV2 } from "./Legend-v2";
import { LegendData } from "./LegendData";

const widgetLegendsMap = {
    legend: LegendV2,
    legendData: LegendData,
    legendReact: Legends,
    legendReactWWO: AutoCompareLegends,
    legendBulletReact: BulletLegends,
};

const customLegendsMap = {
    ["benchmark-compare-legend"]: BenchmarkCompareLegendTemplate,
    comparedDurationLegend: ComparedDurationLegendTemplate,
    ["engagement-widgets-legend-compare"]: EngagementWidgetsLegendCompareTemplate,
    ["engagement-widgets-legend-single"]: EngagementWidgetsLegendSingleTemplate,
    ["mmx-data-legend"]: MmxDataLegendTemplate,
};

// Extracts the file name from the custom legend path string, without extension
const getCustomLegendComponent = (customTemplatePath) => {
    const fileNameStartPosition = customTemplatePath.lastIndexOf("/");
    const templateName = customTemplatePath.substring(fileNameStartPosition + 1, customTemplatePath.length - 5);
    return customLegendsMap[templateName];
};

const getWidgetLegendComponent = ({
    widget,
    customTemplate,
    useBulletLegends,
    items,
    legendData,
    useNewLegends,
    trackWidgetType,
    reactLegendComponent,
}) => {
    const isSingleMode = proNavigator.getParams().key ? proNavigator.getParams().key.split(",").length === 1 : null;
    const HasAutoCompareList = isSingleMode && items.length > 1;
    let widgetLegend: any = widgetLegendsMap.legend;

    if (reactLegendComponent) {
        widgetLegend = reactLegendComponent;
    } else if (legendData) {
        widgetLegend = widgetLegendsMap.legendData;
    } else if (customTemplate) {
        widgetLegend = getCustomLegendComponent(customTemplate);
    }

    if (useNewLegends) {
        if (trackWidgetType === "WWOVisitsGraph" && isSingleMode && HasAutoCompareList) {
            widgetLegend = widgetLegendsMap.legendReactWWO;
        } else {
            widgetLegend = widgetLegendsMap.legendReact;
        }
    }

    if (useBulletLegends) {
        widgetLegend = widgetLegendsMap.legendBulletReact;
    }
    return widgetLegend;
};

const getIcon = (item) => item.icon || item.Icon || item.image;

interface ILegendContainer extends IDashboardWidgetInterface {
    showLegendsData?: boolean;
    useBulletLegends?: boolean;
    items: any;
    onSeriesToggle?: boolean;
    legendContainerClass?: string;
    chartConfig?: any;
    customTemplate?: any;
    legendData?: any;
    useNewLegends?: boolean;
    trackName?: string;
    legendsColorSource?: any;
    gridColumnGap?: any;
    trackWidgetType?: string;
    reactLegendComponent?: any;
    className?: string;
}

export const WidgetLegendContainerV2: FC<ILegendContainer> = (props) => {
    const {
        widget,
        items,
        chartConfig,
        legendData,
        legendContainerClass,
        legendsColorSource,
        trackName,
        trackWidgetType,
        onSeriesToggle,
        customTemplate,
        useNewLegends,
        gridColumnGap,
        showLegendsData,
        useBulletLegends,
        reactLegendComponent,
        className,
    } = props;

    const { widgetState, _params } = widget;

    const [legendItems, setLegendItems] = useState([]);

    useEffect(() => {
        if (!_params.pageFilter) {
            setLegendItems(items);
            return;
        }

        const pageFilterParsed = JSON.parse(_params.pageFilter);
        const newItems = pageFilterParsed.map((page) => {
            return {
                name: page.url,
                displayName: page.url,
                image: items.find((item) => item.name === page.url)?.image,
                reverseEllipsis: page.searchType === "exact" || page.searchType === "folder",
            };
        });

        setLegendItems(newItems);
    }, [items]);

    const LegendComponent = getWidgetLegendComponent({
        widget,
        customTemplate,
        useBulletLegends,
        items,
        legendData,
        useNewLegends,
        trackWidgetType,
        reactLegendComponent,
    });

    const colors = useMemo(() => {
        return legendsColorSource ? CHART_COLORS[legendsColorSource] : CHART_COLORS.compareMainColors;
    }, [legendsColorSource]);

    useEffect(() => {
        // fallback in case chosenSites service hasn't finished it's request yet
        if (items?.length > 0 && !getIcon(items[0])) {
            items.forEach((item) => {
                if (item.icon && item.icon.match(/url=[^&]/)) {
                    void fetchWebsiteImageQuery({ website: item.id as string }).then((data) => {
                        item.icon = data.image;
                    });
                }
            });
        }
    }, []);

    const toggleSeries = (item) => {
        const hasValue = (obj, key) => obj.hasOwnProperty(key) && obj[key] !== null;

        if (typeof item.onClick === "function") {
            setLegendItems((prev) => {
                const newItems = prev.map((it) => {
                    if (it === item) {
                        const newState = !it.hidden;
                        it.hidden = newState;
                        it.onClick(newState);
                    }
                    return it;
                });
                return newItems;
            });
            widget.emit(WidgetMessages.WIDGET_UPDATED);
            return;
        }
        if (chartConfig && chartConfig.series) {
            const series = chartConfig.series.find(
                (series) =>
                    (hasValue(series, "key") && series.key === item.id) ||
                    (hasValue(series, "id") && series.id === item.id) ||
                    (hasValue(series, "name") && series.name === item.id) ||
                    (hasValue(series, "name") && series.name === item.name),
            );
            if (series && !item.alwaysDisabled) {
                series.visible = series.visible === false;
                item.hidden = !series.visible;
                SwTrack.all.trackEvent(
                    "Click Filter",
                    item.hidden ? "remove" : "click",
                    trackName + "/" + (item.name || item.Title),
                );
            }
        }
        widget.emit(WidgetMessages.WIDGET_UPDATED);
    };

    const legendComponentProps = {
        legendItems,
        // Todo: Should onToggle be passed here?
        onToggle: onSeriesToggle,
        toggleSeries,
        gridColumnGap,
        legendsGap: gridColumnGap,
        showLegendsData,
        chartConfig,
        loadingData: widgetState < 2,
        legendContainerClass,
        getIcon,
        colors,
    };

    // Todo: this changes the legends based in data - test if needed
    // $scope.$watch("legendItems[0].data", function (val) {
    //     $timeout(function () {
    //         if (val) {
    //             $(".legend-item").removeClass("disabled");
    //             const potentialLegendWidth =
    //                 $($element).parent().innerWidth() -
    //                 $($element)
    //                     .parent()
    //                     .find(".swWidget-utilityGroup--titleRow")
    //                     .innerWidth(),
    //                 elem = $($element).find(".legend-data"),
    //                 elemWidth = elem.innerWidth(),
    //                 elemChilds = elem.find(".item-name"),
    //                 margin = 30;
    //             if (elemWidth > potentialLegendWidth - margin) {
    //                 $scope.legendItems.forEach(function (item, index) {
    //                     if (
    //                         $scope.legendItems.length === 3 &&
    //                         $(elemChilds[index]).text().length > 19
    //                     ) {
    //                         $(elemChilds[index]).css({ maxWidth: "150px" });
    //                     } else if (
    //                         $scope.legendItems.length === 4 &&
    //                         $(elemChilds[index]).text().length > 16
    //                     ) {
    //                         $(elemChilds[index]).css({ maxWidth: "130px" });
    //                     } else if (
    //                         $scope.legendItems.length === 5 &&
    //                         $(elemChilds[index]).text().length > 14
    //                     ) {
    //                         $(elemChilds[index]).css({ maxWidth: "110px" });
    //                     }
    //                     if (
    //                         item.format === "noFractionNumber" ||
    //                         item.format === "decimalNumber"
    //                     ) {
    //                         item.format = "abbrNumber";
    //                     }
    //                 });
    //             }
    //         }
    //     }, 1);
    // });
    // LegendComponent is resolved dynamically (it may be an arbitrary component injected
    // via the reactLegendComponent prop), so it can't be expressed as a static JSX tag.
    return createElement(LegendComponent, { ...legendComponentProps, className });
};
